import React, { useEffect, useMemo, useState } from 'react';
import useAuth from '../../auth/UseAuth';
import {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  Box,
  DialogActions,
  DialogContent,
  Typography,
} from '@mui/material';
import TagIcon from '@mui/icons-material/Tag';
import { DialogOpenButton } from '../../components/DialogOpenButton';
import useMemoTranslation from '../../hooks/UseMemoTranslation';
import { MultipleValueSelector } from '../../components/MultipleValueSelector';
import { AutocompleteValue } from '@mui/base/AutocompleteUnstyled/useAutocomplete';
import { LoadingState, LoadState } from '../../components/LoadingStateUtil';
import { DialogHeader } from '../../components/DialogHeader';
import { LoadingIndicator } from '../../components/LoadingIndicator';
import { ErrorIndicator } from '../../components/ErrorIndicator';
import { CancelButton } from '../../components/CancelButton';
import { PrimaryButton } from '../../components/PrimaryButton';
import {
  AddTagsForSamples,
  DeleteAllTagsForSamples,
  DeleteTagsForSamples,
  SelectedSampleInfo,
} from '../../data/SampleTrackingData';
import { GetSampleTags, Sample, SampleTag } from '../../data/SampleData';
import { SampleTagGridCell } from '../../components/grid/cell/SampleTagGridCell';
import { CustomGridFilterInputMultipleValue } from '../../components/grid/panel/filterPanel/CustomGridFilterInputMultipleValue';
import { GridColDef } from '@mui/x-data-grid-pro';
import { TFunction } from 'i18next';
import { PersistentDialog } from '../../components/PersistentDialog';

export const sampleTagGridColumnDef = (field: string, headerName: string, t: TFunction): GridColDef => {
  return {
    field: field,
    headerName: headerName,
    headerAlign: 'left',
    align: 'left',
    minWidth: 150,
    renderCell: params => <SampleTagGridCell sample={params.row as Sample} />,
    filterable: true,
    filterOperators: [
      {
        value: 'isAnyOf',
        requiresFilterValue: true,
        InputComponent: CustomGridFilterInputMultipleValue,
        getApplyFilterFn: filterItem => {
          if (!filterItem.field || !filterItem.value || !filterItem.operator) {
            return null;
          }

          return params => {
            const sampleTags = params.row.sampleTags || [];
            return sampleTags.some((label: string) => filterItem.value.includes(label));
          };
        },
      },
      {
        value: 'isAllOf',
        label: t('isAllOfFilter'),
        requiresFilterValue: true,
        InputComponent: CustomGridFilterInputMultipleValue,
        getApplyFilterFn: filterItem => {
          if (!filterItem.field || !filterItem.value || !filterItem.operator) {
            return null;
          }

          return params => {
            const sampleTags = params.row.sampleTags || [];
            return sampleTags.every((label: string) => filterItem.value.includes(label));
          };
        },
      },
      {
        value: 'isEmpty',
        label: t('isEmptyFilter'),
        requiresFilterValue: false,
        getApplyFilterFn: filterItem => {
          if (!filterItem.field || !filterItem.operator) {
            return null;
          }

          return params => {
            const sampleTags = params.row.sampleTags || [];
            return sampleTags.length === 0;
          };
        },
      },
      {
        value: 'isNotEmpty',
        label: t('isNotEmptyFilter'),
        requiresFilterValue: false,
        getApplyFilterFn: filterItem => {
          if (!filterItem.field || !filterItem.operator) {
            return null;
          }

          return params => {
            const sampleTags = params.row.sampleTags || [];
            return sampleTags.length > 0;
          };
        },
      },
    ],
  } as GridColDef;
};

type SampleTrackingTagSamplesModalProps = {
  researchProjectId: string;
  selectedSamples: ReadonlyArray<SelectedSampleInfo>;
  onClose?: (hasChanges: boolean) => void;
};

export const SampleTrackingTagSamplesModal = ({
  researchProjectId,
  selectedSamples,
  onClose = () => {},
}: SampleTrackingTagSamplesModalProps) => {
  const { t } = useMemoTranslation();

  const { accessToken } = useAuth();

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [tags, setTags] = useState<ReadonlyArray<SampleTag>>([]);
  const [selectedValue, setSelectedValue] = useState<SampleTag[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>({ status: 'NotStarted' });

  const counts = useMemo(() => {
    const c = Object.fromEntries(tags.map(t => [t.name, 0]));
    selectedSamples.forEach(sample => {
      sample.sampleTags!.forEach(tag => {
        if (tag in c) {
          c[tag] += 1;
        }
      });
    });

    return c;
  }, [selectedSamples, tags]);

  useEffect(() => {
    return LoadState(setLoadingState, async () => {
      const data = await GetSampleTags(accessToken);

      setTags(data);
    });
  }, [accessToken]);

  const onSelectionChange = (
    _: React.SyntheticEvent,
    value: AutocompleteValue<SampleTag, boolean, boolean, boolean>,
    __: AutocompleteChangeReason,
    ___: AutocompleteChangeDetails<SampleTag> | undefined
  ) => {
    setSelectedValue(value as SampleTag[]);
  };

  const resetModal = () => {
    setModalOpen(false);
    setSelectedValue([]);
  };

  const handleClose = async () => {
    onClose(true);
    resetModal();
  };

  const handleCancel = () => {
    onClose(false);
    resetModal();
  };

  const handleSubmitAdd = async (): Promise<void> => {
    LoadState(setLoadingState, async () => {
      const tagRequests = selectedSamples.map(sample => {
        return {
          sampleId: sample.sampleId,
          tags: (selectedValue ?? []).map(t => t.name),
        };
      });

      await AddTagsForSamples(researchProjectId, tagRequests, accessToken);
      await handleClose();
    });
  };

  const handleSubmitRemove = async (): Promise<void> => {
    LoadState(setLoadingState, async () => {
      const tagRequests = selectedSamples.map(sample => {
        return {
          sampleId: sample.sampleId,
          tags: (selectedValue ?? []).map(t => t.name),
        };
      });

      await DeleteTagsForSamples(researchProjectId, tagRequests, accessToken);
      await handleClose();
    });
  };

  const handleSubmitClear = async (): Promise<void> => {
    LoadState(setLoadingState, async () => {
      await DeleteAllTagsForSamples(
        researchProjectId,
        selectedSamples.map(sample => sample.sampleId) as string[],
        accessToken
      );
      await handleClose();
    });
  };

  return (
    <>
      <DialogOpenButton
        title={t('tagSamples')}
        onClick={() => setModalOpen(true)}
        disabled={selectedSamples.length === 0}
      >
        <TagIcon />
      </DialogOpenButton>
      <PersistentDialog open={modalOpen} onClose={handleClose} maxWidth={'xs'}>
        <DialogHeader handleClose={handleCancel}>
          <Typography variant='subtitle1'>{selectedSamples.length + ' Sample(s)'}</Typography>
        </DialogHeader>

        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography sx={{ whiteSpace: 'pre-line' }}>{t('tagSamplesInfo')}</Typography>
            <MultipleValueSelector<SampleTag>
              filterSelectedOptions={true}
              value={selectedValue}
              options={tags}
              getOptionLabel={option => `${(option as SampleTag).name} (${counts[(option as SampleTag).name]})`}
              onChange={onSelectionChange}
              variant={'outlined'}
              loadingState={loadingState}
              label={'Select tag(s)'}
            />
            <LoadingIndicator loadingState={loadingState} margin={'LR'} />
            <ErrorIndicator loadingState={loadingState} />
          </Box>
        </DialogContent>
        <DialogActions>
          <CancelButton onClick={handleCancel} />
          <PrimaryButton disabled={loadingState.status === 'Loading'} onClick={handleSubmitClear}>
            {'Remove All'}
          </PrimaryButton>
          <PrimaryButton
            disabled={loadingState.status === 'Loading' || !selectedValue || selectedValue.length === 0}
            onClick={handleSubmitRemove}
          >
            {'Remove'}
          </PrimaryButton>
          <PrimaryButton
            disabled={loadingState.status === 'Loading' || !selectedValue || selectedValue.length === 0}
            onClick={handleSubmitAdd}
          >
            {'Add'}
          </PrimaryButton>
        </DialogActions>
      </PersistentDialog>
    </>
  );
};
